// The order detail drawer (spec/dashboard.md section 11.5): open from the URL (`?order=<id>`), `GET …/orders/{order}`,
// every field of the order, and the support actions of spec/api-conventions.md section 19.5 — Commit, Claim, Fail — each
// asking a reason (ReasonDialog) and posting `{ reason }`; a 409 names the current status in the toast and re-reads.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ops } from '../../api/operations';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Modal } from '../../ui/Modal';
import { ReasonDialog } from '../../ui/ReasonDialog';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { ACTIONS, asApiError, goldText, priceText, STATUS_TONE, type Order, type OrderAction } from './orders';
import { AlertCircle } from '../../ui/icons';
import { SkeletonRows } from '../../ui/PageStates';

export interface OrderDrawerProps {
  /** the order id of the URL (`?order=`); null keeps the drawer closed */
  orderId: string | null;
  envSlug: string;
  /** the cache prefix of the orders of this environment (`branch:<game>:env:<slug>:orders:`) */
  prefix: string;
  canWrite: boolean;
  onClose: () => void;
}

export function OrderDrawer({ orderId, envSlug, prefix, canWrite, onClose }: OrderDrawerProps) {
  const { org, game, offsets } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const [zone] = useTimeZone();
  const [pending, setPending] = useState<OrderAction | null>(null);
  const [busy, setBusy] = useState<OrderAction | null>(null);
  const key = orderId ? `${prefix}order:${orderId}` : null;
  const orderQ = useQuery(key, () => client.data(ops.getOrder, { path: { org, game: game.slug, env: envSlug, order: orderId ?? '' } }));
  const order = orderQ.data as Order | undefined;
  const error = orderQ.error ? asApiError(orderQ.error) : null;

  if (!orderId) return null;

  const when = (v: string | null | undefined) => formatInstantWithZone(v, zone, { offsets });
  const reread = () => queryCache.invalidate(prefix); // the order and every page of the list

  const run = async (action: OrderAction, reason: string) => {
    if (!order) return;
    const meta = ACTIONS.find((a) => a.action === action)!;
    const op = action === 'commit' ? ops.commitOrder : action === 'claim' ? ops.claimOrder : ops.failOrder;
    setBusy(action);
    try {
      const next = (await client.data(op, { path: { org, game: game.slug, env: envSlug, order: order.id }, body: { reason } })) as unknown as Order | undefined;
      toasts.success(`${meta.label}: order ${order.paymentId} is now ${next?.status ?? meta.to}`);
      reread();
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
      if (err.status === 409) reread();
    } finally {
      setBusy(null);
    }
  };

  const pendingMeta = pending ? ACTIONS.find((a) => a.action === pending)! : null;
  return (
    <>
      <Modal drawer open title={order ? `Order ${order.paymentId}` : 'Order'} onClose={() => !pending && onClose()}className="order-drawer" initialFocus="none">
        <div data-testid="order-drawer" className="order-detail">
          {error ? (
            <div className="banner banner-danger" role="alert">
              <AlertCircle className="banner-icon" aria-hidden="true" />
              <div className="banner-body">
                {error.status === 404 ? `No order “${orderId}” in this environment.` : error.message}
                {error.requestId ? ` (request ${error.requestId})` : ''}
              </div>
              <Button size="s" onClick={() => key && queryCache.invalidate(key)}>
                Retry
              </Button>
            </div>
          ) : !order ? (
            <SkeletonRows rows={6} label="Loading the order" />
          ) : (
            <>
              <div className="stat-strip order-stats">
                <div className="stat">
                  <span className="stat-label">Price</span>
                  <span className="stat-value">{priceText(order)}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Gold</span>
                  <span className="stat-value">{goldText(order)}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Created</span>
                  <span className="stat-value order-stat-time">{when(order.createdAt)}</span>
                </div>
              </div>
              <h3 className="order-section">Order</h3>
              <dl className="kv order-fields">
                <Row label="Status">
                  <span data-testid="order-status">
                    <Badge tone={STATUS_TONE[order.status] ?? 'neutral'}>{order.status}</Badge>
                  </span>
                  {order.test && (
                    <>
                      {' '}
                      <Badge tone="accent">test</Badge>
                    </>
                  )}
                </Row>
                <Row label="Payment id">
                  <code>{order.paymentId}</code>
                </Row>
                <Row label="Order id">
                  <code className="small">{order.id}</code>
                </Row>
                <Row label="Provider">{order.provider}</Row>
                <Row label="Platform">
                  {order.platform} <span className="muted small">(code {String(order.platformCode)})</span>
                </Row>
                <Row label="Game type">{order.gameType ?? '—'}</Row>
                <Row label="External user">{order.externalUserId}</Row>
                <Row label="Player">
                  {order.playerId ? (
                    <Link to={routes.player(org, game.slug, envSlug, order.playerId)} data-testid="order-player-link">
                      {order.playerId}
                    </Link>
                  ) : (
                    '—'
                  )}
                </Row>
                <Row label="SKU">{order.skuId}</Row>
                <Row label="Product">{order.productId}</Row>
                <Row label="Name">{order.name ?? '—'}</Row>
                <Row label="Price">{priceText(order)}</Row>
                <Row label="Gold">{goldText(order)}</Row>
                <Row label="Test">{order.test ? 'yes' : 'no'}</Row>
                <Row label="Release">
                  <code className="small">{order.releaseId}</code>
                </Row>
                <Row label="Counters">
                  <span data-testid="order-counters">
                    post {String(order.postCount)} · put {String(order.putCount)} · complete {String(order.completeCount)}
                  </span>
                </Row>
                <Row label="Revived">{order.revived ? 'yes' : 'no'}</Row>
                <Row label="Reason">{order.reason ?? '—'}</Row>
              </dl>
              <h3 className="order-section">Timeline</h3>
              <dl className="kv order-fields">
                <Row label="Created">{when(order.createdAt)}</Row>
                <Row label="Expires">{when(order.expiresAt)}</Row>
                <Row label="Committed">{when(order.committedAt)}</Row>
                <Row label="Claimed">{when(order.claimedAt)}</Row>
                <Row label="Failed">{when(order.failedAt)}</Row>
                <Row label="Updated">{when(order.updatedAt)}</Row>
              </dl>
              <h3 className="order-section">System</h3>
              <dl className="kv order-fields">
                <Row label="Request id">
                  <code className="small">{order.requestId}</code>
                </Row>
                <Row label="Environment id">
                  <code className="small">{order.environmentId}</code>
                </Row>
                <Row label="Version">{String(order.version)}</Row>
              </dl>
              <div className="modal-footer form-footer order-actions" role="group" aria-label="Order actions">
                {ACTIONS.map((a) => {
                  const allowed = a.from.includes(order.status);
                  return (
                    <Button
                      key={a.action}
                      variant={a.action === 'fail' ? 'danger' : a.action === 'commit' ? 'primary' : 'secondary'}
                      disabled={!allowed}
                      busy={busy === a.action}
                      missingPermission={canWrite ? null : 'players_write'}
                      title={allowed ? `${a.label}: ${order.status} → ${a.to}` : `Only from ${a.from.join(' or ')}; the order is ${order.status}.`}
                      onClick={() => setPending(a.action)}
                    >
                      {a.label}
                    </Button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </Modal>
      {pendingMeta && (
        <ReasonDialog
          key={pendingMeta.action}
          open
          title={`Reason for ${pendingMeta.action}`}
          confirmLabel={pendingMeta.label}
          tone={pendingMeta.action === 'fail' ? 'danger' : 'primary'}
          body={
            <p className="muted small">
              {pendingMeta.label} order <code>{order?.paymentId}</code>: {order?.status} → {pendingMeta.to}. The reason is recorded in the audit log (2–2000 characters).
            </p>
          }
          onResult={(reason) => {
            setPending(null);
            if (reason !== null) void run(pendingMeta.action, reason);
          }}
        />
      )}
    </>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}
